import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { toTitleCase } from '@iotv/datamodel-core';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Avatar, Badge, CardHeader, Tab } from '@mui/material';
import React, { useContext } from 'react';
import { AppMainViewContext } from '../../ContextProviders/AppContextProvider';
import { isAdminRole } from '../../helpers/access';
import { useMostProximateCustomer } from '../../hooks/useMostProximateCustomer';
import { useTabHandlerStringKeys } from '../../hooks/useTabHandler';
import { GenericConfigTab } from '../../v2/components/generic/Control/LFC-Devices/GenericConfigTab';
import { IncidentListCard } from '../common/IncidentListCard';
import NavLink from '../navigation/NavLink';
import { NavSections } from '../navigation/NavSections';
import { DeviceConfigContext } from './Context/deviceConfigContext';
import { ConfigTab, DetailsTab, HistoryTab } from './tabs';
import { DownlinksView } from './tabs/Downlinks/DownLinksView';
import LinksTab from './tabs/LinksTab';
import NetworkAndMessagesTab from './tabs/NetworkAndMessages/NetworkAndMessagesTab';
const DeviceForm = (props) => {
    const { viewObject, contextCustomer, user, userGroupRoles, userSelectedGroup, roleArbitrationInstance, userFunctions, transactionFunctions, allowedActions, additionalTabs, isSmallView, } = props;
    const matchedPrimary = viewObject.matchedPrimary;
    const mostProximateCustomer = useMostProximateCustomer({ item: matchedPrimary });
    // WARN: only use for passing through to additional tabs — everything the default tabs need should already come from props
    const [appContext] = useContext(AppMainViewContext);
    const { state } = useContext(DeviceConfigContext);
    const { device, deviceConfig, entity, deviceHistory, nodeEdgeMapper, deviceEnabledTypeIds, lns } = state;
    const DefaultTabs = {
        Details: () => device ? (_jsx(DetailsTab, { userFunctions,
            transactionFunctions,
            viewObject,
            allowedActions,
            mostProximateCustomer,
            contextObject: undefined,
            userGroupRoles,
            userSelectedGroup,
            user,
            device,
            isSmallView })) : null,
        Links: () => device ? (_jsx(LinksTab, { userFunctions,
            transactionFunctions,
            viewObject,
            contextObject: undefined,
            userGroupRoles,
            userSelectedGroup,
            user,
            device,
            nodeEdgeMapper,
            deviceEnabledTypeIds })) : null,
        Messages: () => device ? (_jsx(NetworkAndMessagesTab, { userFunctions,
            transactionFunctions,
            viewObject,
            mostProximateCustomer,
            contextObject: undefined,
            userGroupRoles,
            userSelectedGroup,
            user,
            device,
            lns })) : null,
        Downlinks: () => (device ? _jsx(DownlinksView, {}) : null),
        Incidents: () => device ? (_jsx(IncidentListCard, { viewObject,
            contextCustomer,
            user,
            userGroupRoles,
            userSelectedGroup,
            roleArbitrationInstance,
            userFunctions,
            transactionFunctions,
            isSmallView })) : null,
        History: () => (device ? _jsx(HistoryTab, { device, deviceHistory }) : null),
    };
    if (isAdminRole(userSelectedGroup)) {
        DefaultTabs.Config = () => (device ? _jsx(ConfigTab, { device, config: deviceConfig }) : null);
        DefaultTabs['Advanced Config'] = () => (device ? _jsx(GenericConfigTab, { isSmallView }) : null);
    }
    /** all the externally-defined tabs */
    const externalTabDefinitions = Object.fromEntries(Object.entries(additionalTabs ?? {}).map(([tabName, Komponent]) => [tabName, Komponent ? () => _jsx(Komponent, { ...appContext, ...props }) : null]));
    /** tabs that are in addition to the base LSC tabs */
    const additionalTabDefinitions = Object.fromEntries(Object.entries(externalTabDefinitions).filter(([name]) => !Object.keys(DefaultTabs).includes(name)));
    /** tabs that will override base LSC tabs (including with `null`, thus effectively hiding / disabling them) */
    const overriddenTabDefinitions = Object.fromEntries(Object.entries(DefaultTabs).map(([name, Komponent]) => [name, externalTabDefinitions[name] !== undefined ? externalTabDefinitions[name] : Komponent]));
    const tabDefinitions = {
        ...additionalTabDefinitions,
        ...overriddenTabDefinitions,
    };
    const tabsToDisplay = Object.fromEntries(Object.entries(tabDefinitions).filter(([_, definition]) => definition !== null));
    const { state: [[tabValue, setTabValue]], handlers: { handleTabChange }, } = useTabHandlerStringKeys(tabsToDisplay);
    return (_jsxs(_Fragment, { children: [_jsx(CardHeader, { avatar: _jsx(NavLink, { filter: `${NavSections.THINGVIEW}/${matchedPrimary?.type}/${matchedPrimary?.id}`, children: _jsx(Badge, { color: "primary", children: _jsx(Avatar, { "aria-label": "recipe", children: matchedPrimary ? (matchedPrimary.name ? matchedPrimary.name.substr(0, 1).toUpperCase() : `${matchedPrimary.type} ?`) : '??' }) }) }), title: matchedPrimary?.name, subheader: matchedPrimary?.breed }), device && (_jsxs(TabContext, { value: tabValue, children: [_jsx(TabList, { onChange: handleTabChange, children: Object.entries(tabsToDisplay).map(([name], i) => (_jsx(Tab, { value: name, label: toTitleCase(name) }, `tabHeader_${i}`))) }), Object.entries(tabsToDisplay).map(([name, tab]) => (_jsx(TabPanel, { value: name, sx: { padding: 0, paddingBlockStart: 1 }, children: tab() }, `tabBase_${name}`)))] }))] }));
};
export default DeviceForm;
